import axios from 'axios';
import { toast } from '../../../components/Common/Toast';
import Swal from 'sweetalert2';
import config from '../../../config';

export const useTeamOperations = (token, user, teams, setTeams, selectedTeam, setSelectedTeam, teamMembers, setTeamMembers, setShowTeamFormModal, teamToEdit, setTeamToEdit, setShowAddMemberOptions, setLoading, setFriends) => {
  
  const fetchTeams = async () => {
    try {
      const res = await fetch(`${config.baseURL}/api/teams/user-teams`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setTeams(data.teams || []);
    } catch (err) { toast.error('Failed to load teams'); }
  };

  const fetchFriends = async () => {
    try {
      const { data } = await axios.get(`${config.baseURL}/api/matches/friends-list`, { params: { user_id: user?.user_id } });
      if (data.success) setFriends(data.users || []);
    } catch (err) { console.error('Error fetching friends:', err); }
  };

  const fetchTeamMembers = async (teamId) => {
    try {
      const res = await fetch(`${config.baseURL}/api/teams/${teamId}/members`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setTeamMembers(data.members || []);
    } catch (err) { toast.error('Failed to load team members'); }
  };

  const handleCreateTeam = async (formData) => {
    try {
      setLoading(true);
      const res = await fetch(`${config.baseURL}/api/teams/create`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(formData) });
      const data = await res.json();
      if (data.success) {
        toast.success('Team created successfully');
       setTeams(prev => [data.team, ...prev]);
        setShowTeamFormModal(false);
        setTeamToEdit(null);
        setSelectedTeam(data.team);
        fetchTeamMembers(data.team.team_id);
      } else toast.error(data.message || 'Failed to create team');
    } catch (err) { toast.error('Failed to create team'); }
    finally { setLoading(false); }
  };

 const handleUpdateTeam = async (formData) => {
  if (!teamToEdit) return;

  try {
    setLoading(true);

    const res = await fetch(`${config.baseURL}/api/teams/${teamToEdit.team_id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(formData)
    });

    const data = await res.json();

    if (data.success) {
      toast.success('Team updated successfully');

      const updatedTeam = data.team;

      setTeams(prevTeams =>
        prevTeams.map(team =>
          String(team.team_id) === String(updatedTeam.team_id)
            ? { ...team, ...updatedTeam }
            : team
        )
      );

      setSelectedTeam(prevSelected =>
        String(prevSelected?.team_id) === String(updatedTeam.team_id)
          ? { ...prevSelected, ...updatedTeam }
          : prevSelected
      );

      setShowTeamFormModal(false);
      setTeamToEdit(null);
    } else {
      toast.error(data.message || 'Failed to update team');
    }
  } catch (err) {
    console.error('Failed to update team:', err);
    toast.error('Failed to update team');
  } finally {
    setLoading(false);
  }
};
  const handleDeleteTeam = async (teamId) => {
    try {
      const res = await fetch(`${config.baseURL}/api/teams/${teamId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) {
        toast.success('Team deleted successfully');
        setTeams(teams.filter(team => team.team_id !== teamId));
        if (selectedTeam?.team_id === teamId) {
          setSelectedTeam(null);
          setTeamMembers([]);
        }
      } else toast.error(data.message || 'Failed to delete team');
    } catch (err) { toast.error('Failed to delete team'); }
  };

  const handleAddMember = async (friendId) => {
  if (!selectedTeam) return toast.error('Please select a team first');

  try {
    setLoading(true);

    const userIds = Array.isArray(friendId) ? friendId : [friendId];

    const res = await fetch(`${config.baseURL}/api/teams/add-member`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        team_id: selectedTeam.team_id,
        user_id: userIds
      })
    });

    const data = await res.json();

    if (data.success) {
      const addedMembers = data.members || (data.member ? [data.member] : []);

      toast.success(
        addedMembers.length > 1
          ? 'Members added to team successfully'
          : 'Member added to team successfully'
      );

      setTeamMembers(prev => [...addedMembers, ...prev]);

      setShowAddMemberOptions(false);

      setTeams(prev =>
        prev.map(team =>
          team.team_id === selectedTeam.team_id
            ? {
                ...team,
                member_count: (team.member_count || 0) + addedMembers.length
              }
            : team
        )
      );

      setSelectedTeam(prev =>
        prev
          ? {
              ...prev,
              member_count: (prev.member_count || 0) + addedMembers.length
            }
          : prev
      );
    } else {
      toast.error(data.message || 'Failed to add member');
    }
  } catch (err) {
    toast.error('Failed to add member');
  } finally {
    setLoading(false);
  }
};

  const handleRemoveMember = async (teamMemberId) => {
    const result = await Swal.fire({ title: 'Are you sure?', text: "You won't be able to revert this!", icon: 'warning', showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Yes, delete it!' });
    if (result.isConfirmed) {
      try {
        const res = await fetch(`${config.baseURL}/api/teams/member/${teamMemberId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (data.success) {
          toast.success('Member removed successfully');
          setTeamMembers(teamMembers.filter(member => member.team_member_id !== teamMemberId));
          setTeams(teams.map(team => team.team_id === selectedTeam.team_id ? { ...team, member_count: Math.max(0, (team.member_count || 1) - 1) } : team));
          setSelectedTeam({ ...selectedTeam, member_count: Math.max(0, (selectedTeam.member_count || 1) - 1) });
        }
      } catch (err) { toast.error('Failed to remove member'); }
    }
  };

  const handleExitTeam = async (teamId) => {
    try {
      const res = await fetch(`${config.baseURL}/api/teams/${teamId}/members`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) {
        const currentUserMember = data.members.find(member => member.user_id === user?.user_id);
        if (!currentUserMember) return toast.error('You are not a member of this team');
        const deleteRes = await fetch(`${config.baseURL}/api/teams/member/${currentUserMember.team_member_id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        const deleteData = await deleteRes.json();
        if (deleteData.success) {
          toast.success('You have exited the team successfully');
          setTeams(teams.filter(team => team.team_id !== teamId));
          if (selectedTeam?.team_id === teamId) {
            setSelectedTeam(null);
            setTeamMembers([]);
          }
        } else toast.error(deleteData.message || 'Failed to exit team');
      }
    } catch (err) { toast.error('Failed to exit team'); }
  };

  return {
    fetchTeams,
    fetchFriends,
    fetchTeamMembers,
    handleCreateTeam,
    handleUpdateTeam,
    handleDeleteTeam,
    handleAddMember,
    handleRemoveMember,
    handleExitTeam
  };
};